/**
 * Shop Related Pages
 * E-commerce pages for CatPals+ handcrafted items shop - Stripe payments
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShoppingBag, CheckCircle, X, ArrowRight, CreditCard, Loader2 } from "lucide-react";

import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";

import { API } from "../lib/api";

const ShopPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({ name: "", email: "", address: "" });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Check if returning from cancelled checkout
    if (searchParams.get("cancelled") === "true") {
      toast.info("Checkout was cancelled. Your cart is still here!");
    }
    
    axios.get(`${API}/products`)
      .then(r => setProducts(r.data))
      .catch(() => toast.error("Failed to load products"))
      .finally(() => setLoading(false));
  }, [searchParams]);

  const categories = [
    { id: "all", label: "All Items" },
    { id: "toys", label: "Cat Toys" },
    { id: "accessories", label: "Accessories" },
    { id: "home", label: "Home & Gifts" },
    { id: "clothing", label: "Clothing" }
  ];

  const filteredProducts = selectedCategory === "all" 
    ? products 
    : products.filter(p => p.category === selectedCategory);

  const handlePurchase = async () => {
    if (!selectedProduct) return;
    if (!customerInfo.name || !customerInfo.email || !customerInfo.address) {
      toast.error("Please fill in all delivery details");
      return;
    }
    setCheckoutLoading(true);
    try {
      // Use Stripe checkout
      const res = await axios.post(`${API}/stripe/checkout/product`, {
        product_id: selectedProduct.product_id,
        product_name: selectedProduct.name,
        price: selectedProduct.price,
        quantity: 1,
        customer_name: customerInfo.name,
        customer_email: customerInfo.email,
        shipping_address: customerInfo.address,
        origin_url: window.location.origin
      }, { withCredentials: true });
      
      // Redirect to Stripe Checkout
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to process checkout");
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-36 bg-stone-50" data-testid="shop-page">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <Badge className="bg-amber-100 text-amber-700 mb-4">Handcrafted with Love</Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">CatPals+ Shop</h1>
          <p className="text-stone-600 max-w-2xl mx-auto">
            Every purchase supports our rescue cats! Browse our handcrafted items, all made with love.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 justify-center mb-8">
          {categories.map(cat => (
            <Button
              key={cat.id}
              variant={selectedCategory === cat.id ? "default" : "outline"}
              onClick={() => setSelectedCategory(cat.id)}
              className={`rounded-full ${selectedCategory === cat.id ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
            >
              {cat.label}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full mx-auto" />
          </div>
        ) : filteredProducts.length === 0 ? (
          <Card className="max-w-md mx-auto text-center p-8">
            <ShoppingBag className="w-12 h-12 text-stone-400 mx-auto mb-4" />
            <p className="text-stone-600">No products found in this category.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map(product => (
              <Card key={product.product_id} className="overflow-hidden hover:shadow-lg transition-shadow group">
                <div className="aspect-square relative overflow-hidden bg-stone-100">
                  {(product.image_base64 || product.image_url) ? (
                    <img src={product.image_base64 ? `data:${product.image_mime_type || 'image/jpeg'};base64,${product.image_base64}` : product.image_url} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ShoppingBag className="w-16 h-16 text-stone-300" />
                    </div>
                  )}
                  {!product.in_stock && (
                    <Badge className="absolute top-3 right-3 bg-red-600">Sold Out</Badge>
                  )}
                </div>
                <CardContent className="p-4">
                  <Badge className="bg-stone-100 text-stone-600 mb-2">{product.category}</Badge>
                  <h3 className="font-bold text-lg text-stone-900 mb-1">{product.name}</h3>
                  <p className="text-sm text-stone-600 mb-3 line-clamp-2">{product.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xl font-bold text-amber-600">£{product.price?.toFixed(2)}</span>
                    <Button 
                      onClick={() => setSelectedProduct(product)}
                      disabled={!product.in_stock}
                      className="bg-amber-600 hover:bg-amber-700 rounded-full"
                    >
                      Buy Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Checkout Dialog */}
        <Dialog open={!!selectedProduct} onOpenChange={() => setSelectedProduct(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Complete Your Purchase</DialogTitle>
              <DialogDescription>
                {selectedProduct?.name} - £{selectedProduct?.price?.toFixed(2)}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input 
                  id="name" 
                  value={customerInfo.name}
                  onChange={e => setCustomerInfo({...customerInfo, name: e.target.value})}
                  placeholder="Your name"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input 
                  id="email" 
                  type="email"
                  value={customerInfo.email}
                  onChange={e => setCustomerInfo({...customerInfo, email: e.target.value})}
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <Label htmlFor="address">Delivery Address</Label>
                <Input 
                  id="address" 
                  value={customerInfo.address}
                  onChange={e => setCustomerInfo({...customerInfo, address: e.target.value})}
                  placeholder="Full delivery address"
                />
              </div>
            </div>
            <DialogFooter className="mt-4 flex-col gap-2">
              <Button variant="outline" onClick={() => setSelectedProduct(null)} className="w-full sm:w-auto">Cancel</Button>
              <Button 
                onClick={handlePurchase} 
                disabled={checkoutLoading}
                className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto"
              >
                {checkoutLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Redirecting to Checkout...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Pay £{selectedProduct?.price?.toFixed(2)} with Card
                  </>
                )}
              </Button>
              <p className="text-xs text-stone-500 text-center w-full">Secure checkout powered by Stripe</p>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

const ShopSuccessPage = () => {
  const [status, setStatus] = useState("checking");
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const orderId = params.get("order_id");
  const paymentId = params.get("paymentId");
  const payerId = params.get("PayerID");
  const executedRef = useRef(false);

  useEffect(() => {
    if (executedRef.current) return;
    executedRef.current = true;

    const executePayment = async () => {
      // If we have PayPal params, execute the payment
      if (paymentId && payerId && orderId) {
        try {
          await axios.post(`${API}/shop/execute`, {
            paymentId,
            PayerID: payerId,
            order_id: orderId
          });
          setStatus("success");
        } catch (err) {
          console.error(err);
          setStatus("error");
        }
      } else if (orderId) {
        // Check if order already exists (user refreshed)
        setStatus("success");
      } else {
        setStatus("error");
      }
    };
    executePayment();
  }, [paymentId, payerId, orderId]);

  return (
    <div className="min-h-screen pt-36 flex items-center justify-center bg-stone-50" data-testid="shop-success-page">
      <Card className="max-w-md mx-auto text-center p-8">
        <CardContent>
          {status === "checking" && (
            <div>
              <div className="animate-spin w-12 h-12 border-4 border-amber-600 border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-stone-600">Processing your order...</p>
            </div>
          )}
          {status === "success" && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-stone-900 mb-2">Order Confirmed!</h2>
              <p className="text-stone-600 mb-6">
                Thank you for your purchase! You'll receive a confirmation email shortly.
              </p>
              <Button onClick={() => navigate("/shop")} className="bg-amber-600 hover:bg-amber-700 rounded-full">
                Continue Shopping
              </Button>
            </>
          )}
          {status === "error" && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-stone-900 mb-2">Something went wrong</h2>
              <p className="text-stone-600 mb-6">
                We couldn't process your order. Please try again or contact us.
              </p>
              <Button onClick={() => navigate("/shop")} variant="outline" className="rounded-full">
                Back to Shop
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export { ShopPage, ShopSuccessPage };
